import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, UserRound, Stethoscope, Plus, X, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { listAccounts, forgetAccount } from "@/lib/accounts";
import { RoleSwitch } from "@/components/header/RoleSwitch";

const RoleIcon = ({ role, className }) => (role === "coach" ? <Stethoscope className={className} /> : <UserRound className={className} />);

function PasswordModal({ account, onClose }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      try { await api.post("/auth/logout"); } catch { /* already signed out elsewhere */ }
      const u = await login(account.email, pw);
      onClose();
      if (u.mfa_required) { navigate("/login", { state: { mfaToken: u.mfa_token, email: account.email } }); return; }
      toast.success(`Switched to ${u.name} (${u.role})`);
      navigate(u.role === "clinician" ? "/clinician" : "/client");
    } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-stone-900/50 backdrop-blur-sm p-4" data-testid="switch-modal">
      <form onSubmit={submit} className="warm-card p-6 w-full max-w-sm space-y-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800">Switch account</div>
          <h3 className="font-display text-lg font-bold text-stone-900 mt-1">{account.name}</h3>
          <div className="text-xs text-stone-500">{account.email} · {account.role}</div>
        </div>
        <input type="password" autoFocus required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password for this account" data-testid="switch-password"
          className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-amber-600 outline-none" />
        {err && <p className="text-sm text-red-700" data-testid="switch-error">{err}</p>}
        <div className="flex gap-2">
          <button type="button" onClick={onClose} data-testid="switch-cancel" className="flex-1 px-4 py-2.5 rounded-full border border-stone-300 text-sm font-semibold text-stone-700">Cancel</button>
          <button type="submit" disabled={busy} data-testid="switch-submit" className="flex-1 amber-btn text-sm">{busy ? "Switching…" : "Switch"}</button>
        </div>
      </form>
    </div>
  );
}

export function AccountSwitcher({ user, compact = false }) {
  const { logout } = useAuth();
  const tid = (base) => (compact ? `mobile-${base}` : base);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [accounts, setAccounts] = useState(listAccounts);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);
  const others = accounts.filter((a) => a.email !== user.email);
  const addAnother = async () => { setOpen(false); await logout(); navigate("/login"); };
  const forget = (email) => { forgetAccount(email); setAccounts(listAccounts()); };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setAccounts(listAccounts()); setOpen((v) => !v); }} data-testid={tid("account-switcher")} aria-expanded={open}
        className={`inline-flex items-center gap-2 rounded-full px-2 py-1 hover:bg-stone-100 transition-colors ${compact ? "w-full justify-between" : ""}`}>
        <span className="text-left">
          <span className="block text-sm font-semibold text-stone-800" data-testid={tid("header-user-name")}>{user.name}</span>
          <span className="block text-xs text-stone-500">{user.role === "clinician" ? "Coach" : user.is_coach ? "Member · coach tools on" : "Member"}</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-stone-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className={`${compact ? "relative mt-2" : "absolute right-0 mt-2"} w-72 warm-card p-2 shadow-xl z-[60] whitespace-normal`} data-testid={tid("account-menu")}>
          <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-stone-500 inline-flex items-center gap-1.5"><ArrowLeftRight className="w-3 h-3" /> Switch account</div>
          <RoleSwitch user={user} tid={tid} onDone={() => setOpen(false)} />
          {others.length === 0 && <div className="px-3 pb-2 text-xs text-stone-500" data-testid={tid("account-menu-empty")}>Other emails you sign into on this device will appear here for quick switching.</div>}
          {others.map((a) => (
            <div key={a.email} className="flex items-center gap-1 rounded-xl hover:bg-stone-50">
              <button onClick={() => { setOpen(false); setTarget(a); }} data-testid={tid(`switch-account-${a.role}`)} className="flex-1 flex items-center gap-3 px-3 py-2 text-left">
                <span className={`w-8 h-8 rounded-xl grid place-items-center ${a.role === "clinician" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}><RoleIcon role={a.role} className="w-4 h-4" /></span>
                <span className="min-w-0"><span className="block text-sm font-semibold text-stone-800 truncate">{a.name}</span><span className="block text-[11px] text-stone-500 truncate">{a.email} · {a.role}</span></span>
              </button>
              <button onClick={() => forget(a.email)} data-testid={tid(`forget-account-${a.role}`)} title="Forget this account on this device" className="p-2 text-stone-400 hover:text-red-700"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <button onClick={addAnother} data-testid={tid("add-account")} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left text-sm font-semibold text-amber-800 hover:bg-amber-50">
            <span className="w-8 h-8 rounded-xl grid place-items-center bg-stone-100"><Plus className="w-4 h-4" /></span> Sign into another account
          </button>
          <div className="px-3 pt-1 pb-2 text-[10px] text-stone-400">Switching asks for that account's password. Accounts are remembered on this device only.</div>
        </div>
      )}
      {target && <PasswordModal account={target} onClose={() => setTarget(null)} />}
    </div>
  );
}
